import { Loader2 } from "lucide-react";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import logoQuickbooks from "@/assets/logo-quickbooks.png";
import {
  useGetQBOStatus,
  usePushInvoiceToQBO,
  usePushEstimateToQBO,
  useGetQBOInvoiceSyncStatus,
  useGetQBOEstimateSyncStatus,
} from "@workspace/api-client-react";

type Props =
  | { kind: "invoice"; id: number; testId?: string }
  | { kind: "estimate"; id: number; testId?: string };

function extractError(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  return e?.response?.data?.error || e?.message || "Unknown error";
}

/**
 * Row-level "Push to QuickBooks" dropdown item that mirrors the detail-page
 * QuickBooksPushButton behavior: looks up per-record sync status to flip the
 * label between "Push" and "Update", uses matching success toast wording,
 * shows a pending spinner, and respects needsReconnect.
 *
 * Only render this when the QBO connection is established (caller checks
 * `qboPush.connected` from `useQuickBooksPush`).
 */
export function QuickBooksPushMenuItem(props: Props) {
  const { toast } = useToast();
  const { data: status } = useGetQBOStatus();

  const isInvoice = props.kind === "invoice";
  const connected = status?.connected === true;
  const needsReconnect =
    (status as { needsReconnect?: boolean } | undefined)?.needsReconnect === true;

  const pushInvoice = usePushInvoiceToQBO();
  const pushEstimate = usePushEstimateToQBO();

  // These hooks are mounted with the dropdown content; they only fire while
  // the menu is open (Radix unmounts content on close).
  const invoiceSync = useGetQBOInvoiceSyncStatus(isInvoice ? props.id : undefined, {
    enabled: isInvoice && connected,
  });
  const estimateSync = useGetQBOEstimateSyncStatus(!isInvoice ? props.id : undefined, {
    enabled: !isInvoice && connected,
  });
  const syncData = isInvoice ? invoiceSync.data : estimateSync.data;
  const hasPushed = !!syncData?.externalId || !!syncData?.lastSuccess;
  const isPending = isInvoice ? pushInvoice.isPending : pushEstimate.isPending;

  const label = hasPushed ? "Update in QuickBooks" : "Push to QuickBooks";

  const onClick = async (e: Event) => {
    // Keep menu open during await so the spinner is visible.
    e.preventDefault();
    try {
      if (isInvoice) await pushInvoice.mutateAsync(props.id);
      else await pushEstimate.mutateAsync(props.id);
      toast({
        title: hasPushed ? "Updated in QuickBooks" : "Pushed to QuickBooks",
        description: isInvoice ? "Invoice synced." : "Estimate synced.",
      });
    } catch (err) {
      toast({
        title: "QuickBooks push failed",
        description: extractError(err),
        variant: "destructive",
      });
    }
  };

  return (
    <DropdownMenuItem
      disabled={isPending || needsReconnect}
      onSelect={onClick}
      data-testid={props.testId ?? `menu-qbo-push-${props.kind}-${props.id}`}
      title={needsReconnect ? "Reconnect QuickBooks in Integrations to push." : undefined}
    >
      {isPending ? (
        <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
      ) : (
        <img src={logoQuickbooks} alt="QuickBooks" className="w-3.5 h-3.5 mr-2 object-contain" />
      )}
      {label}
    </DropdownMenuItem>
  );
}
